import React, { useState } from 'react'
import { CustomDialog, CustomTabs, IOSSwitch } from '../../../components/StyledComponents'
import { DialogActions, DialogContent, DialogTitle, FormControl, Tab } from '@mui/material'
import DefaultTooltip from '../../../components/DefaultTooltip'
import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg'
import { ControlpanelSecondaryButton, ControlpanelSuccessButton, GrayIconButton } from '../../../components/StyledButton'
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next'
import PrimaryDivider from '../../../components/PrimaryDivider'
import { PropTypes } from 'prop-types';
import RefreshIcon from '@mui/icons-material/Refresh';
import CustomInput from '../../../components/CustomInput'
import CustomSelect from './../../../components/CustomSelect';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import SaveIcon from '@mui/icons-material/Save';

const DeviceSpecificSettingsDialog = (props) => {
    const { t } = useTranslation()

    const handleCloseDialog = () => {
        props.handleClose()
    }

    const [tabValue, setTabValue] = useState(0);
    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    const [clientFormData, setClientFormData] = useState({
        resultDialogTimeout: 6,
        continueCaptureWhenNoFocus: false,
        enableLicensing: false,
        showWorkHoursInClients: false,
        allowAutomaticPeripheralEnablingOfNewClient: false,
        offlineOperations: false,
        manualCaptureStart: false,
        manualCaptureOptions: "select-start-button",
        offlineDataSynchronizationInterval: "5",
        resetOfflineClientPassword: "admin",
        allowPredefinedLocation: "always-use-gps",
        hatDetectionFeature: false,
        hatDetectionThreshold: 48,
        hideUserListOnClientApps: false,
        kioskModeOperation: false,
        cancelEventButtonOnClientEventFeedback: true,
        automaticallySynchronizeOfflineData: false,
        selfEnrollTemplates: false,
        useInBuiltBiometricVerificationOnPersonalClients: false,
        multiFaceMode: true,
    })

    const [biometricFormData, setBiometricFormData] = useState({
        faceLivenessModeForClientApplications: "none",
        passiveLivenessQualityThreshold: 60,
        livenessThreshold: 50,
        faceQuality: 30,
        maskDetection: "disable",
        manualFaceSelection: false,
        serverSideFaceLivenessChecking: false,
        serverFaceLivenessConfidenceThreshold: 50,
    })

    const [validationMessages, setValidationMessages] = useState({
        resetOfflineClientPassword: "",
    })

    const [isError, setIsError] = useState({
        resetOfflineClientPassword: false,

    })

    const [isSuccess, setIsSuccess] = useState({
        resetOfflineClientPassword: false,
    })


    const CustomTabPanel = (props) => {
        const { children, value, index, ...other } = props;
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`peripherals-${index}`}
                aria-labelledby={`peripherals-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <div className='mt-3'>
                        {children}
                    </div>
                )}
            </div>
        );
    }

    CustomTabPanel.propTypes = {
        children: PropTypes.node,
        index: PropTypes.number.isRequired,
        value: PropTypes.number.isRequired,
    };

    const a11yProps = (index) => {
        return {
            id: `peripherals-tab-${index}`,
            'aria-controls': `peripherals-tabpanel-${index}`,
        };
    }

    const handleSwitchChange = (e, formData, setFormData) => {
        const { name, checked } = e.target;
        setFormData({
            ...formData,
            [name]: checked,
        });
    };

    const handleInputChange = (e, formData, setFormData) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }))
    }

    const offlineDataSynchronizationIntervalData = [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "20", label: "20 min" },
        { value: "30", label: "30 min" },
        { value: "60", label: "1 hour" },
        { value: "120", label: "2 hours" },
        { value: "180", label: "3 hours" },
        { value: "300", label: "5 hours" },
        { value: "720", label: "12 hours" },
        { value: "1440", label: "24 hour" },
    ]

    const allowPredefinedLocationData = [
        { value: "always-use-gps", label: "Always use GPS" },
        { value: "prefer-gps-over-predefined", label: "Prefer GPS over predefined" },
        { value: "always-use-predefined", label: "Always use predefined" },
    ]

    const maskDetectionData = [
        { value: "required", label: "Required" },
        { value: "detect", label: "Detect" },
        { value: "disable", label: "Disable" },
    ]

    const faceLivenessModeForClientApplicationsData = [
        { value: "none", label: "None" },
        { value: "passive", label: "Passive" },
        { value: "active", label: "Active" },
        { value: "passive-and-active", label: "Passive and active" },
        { value: "simple", label: "Simple" },
        { value: "custom", label: "Custom" },
    ]

    return (
        <CustomDialog
            maxWidth={"xl"}
            open={props.open}
            onClose={handleCloseDialog}
            keepMounted
        >
            <DialogTitle>
                <div className='flex justify-between'>
                    <div className='flex items-center gap-4'>
                        <p className='text-xl font-semibold '>{t('common.device-specific-settings')}</p>
                        <DefaultTooltip
                            readMore={true}
                            title={t('tool-tip.device-settings')}
                            content={
                                <div className='flex flex-col gap-3'>
                                    <p>{t('tool-tip.this-view-allow-to-add-client-or-biometric-settings')}.</p>
                                </div>
                            }>
                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                        </DefaultTooltip>
                    </div>
                    <GrayIconButton onClick={handleCloseDialog}>
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                <CustomTabs
                    variant="fullWidth"
                    indicatorColor='none'
                    value={tabValue}
                    onChange={handleChangeTab}>
                    <Tab
                        label={
                            <div className='flex flex-col md:flex-row items-center gap-1'>
                                <p className='text-sm'>{t('common.client')}</p>
                            </div>
                        } {...a11yProps(0)} disableRipple />
                    <Tab
                        label={
                            <div className='flex flex-col md:flex-row items-center gap-1'>
                                <p className='text-sm'>{t('common.biometric')}</p>
                            </div>
                        } {...a11yProps(0)} disableRipple />
                </CustomTabs>
                <CustomTabPanel value={tabValue} index={0}>
                    <div className='client-tab flex flex-col gap-5'>
                        <div className='flex flex-col gap-5'>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center '>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.result-dialog-timeout')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.result-dialog-timeout')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.timeout-for-check-in-check-out-result-dialog')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60'>
                                        <CustomInput
                                            labelEnable={false}
                                            inputType="number"
                                            name="resultDialogTimeout"
                                            id="resultDialogTimeout"
                                            value={clientFormData.resultDialogTimeout}
                                            handleInputChange={(e) => handleInputChange(e, clientFormData, setClientFormData)}
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.closing-timeout-for-device-check-in-check-out-result-dialog')}</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.continue-capture-when-no-focus')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.continue-capturing-when-app-has-no-focus')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.enable-to-keep-capturing-when-app-is-not-in-focus')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.continueCaptureWhenNoFocus}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="continueCaptureWhenNoFocus"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.hat-detection-threshold')}</p>

                                    </div>
                                    <div className='min-w-60'>
                                        <CustomInput
                                            labelEnable={false}
                                            inputType="number"
                                            name="resultDialogTimeout"
                                            id="resultDialogTimeout"
                                            value={clientFormData.hatDetectionThreshold}
                                            handleInputChange={(e) => handleInputChange(e, clientFormData, setClientFormData)}
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.offline-data-synchronization-interval')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.offline-data-synchronize-interval')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.data-synchronize-interval-when-the-offline-data-synchronize-interval-setting-is-enabled')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-32'>
                                        <FormControl className='w-full'>
                                            <CustomSelect
                                                name="offlineDataSynchronizationInterval"
                                                id='offlineDataSynchronizationInterval'
                                                value={clientFormData.offlineDataSynchronizationInterval}
                                                onChange={(e) => handleInputChange(e, clientFormData, setClientFormData)}
                                                options={offlineDataSynchronizationIntervalData}
                                                defaultValue="5"
                                                dataType="offlineDataSynchronizationInterval"
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.time-interval-between-offline-data-synchronizations')}.
                                    </p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.offline-data-synchronizations-interval')}</p>
                                    </div>
                                    <div className='min-w-60'>
                                        <FormControl className='w-full'>
                                            <CustomSelect
                                                name="allowPredefinedLocation"
                                                id='allowPredefinedLocation'
                                                value={clientFormData.allowPredefinedLocation}
                                                onChange={(e) => handleInputChange(e, clientFormData, setClientFormData)}
                                                options={allowPredefinedLocationData}
                                                defaultValue="always-use-gps"
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.reset-offine-client-password')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.reset-offline-client-password')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.reset-password-for-the-control-panel-login-in-facialsense')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60'>
                                        <div className="relative">
                                            <div className='flex items-center'>
                                                <input type="text"
                                                    placeholder="New password"
                                                    name="resetOfflineClientPassword"
                                                    id="resetOfflineClientPassword"
                                                    className={`form-control !rounded-r-none ${isError.resetOfflineClientPassword && validationMessages.resetOfflineClientPassword ? "invalid" : ""} 
                    ${isSuccess.resetOfflineClientPassword ? "success" : ""}
                    ${(isError.resetOfflineClientPassword && validationMessages.resetOfflineClientPassword) || isSuccess.resetOfflineClientPassword ? "pr-12" : ""}`
                                                    }
                                                    onChange={(e) => handleInputChange(e, clientFormData, setClientFormData)}
                                                    value={clientFormData.resetOfflineClientPassword} />
                                                <button className='bg-[#6B7280] py-[12.5px] px-3 rounded-r-md'>
                                                    <p className='text-white'>{t('button.reset')}</p>
                                                </button>
                                            </div>
                                            {isSuccess.resetOfflineClientPassword && (
                                                <p className='text-[14px] text-red-500 mt-1'>{validationMessages.resetOfflineClientPassword}</p>
                                            )}
                                            {isSuccess.resetOfflineClientPassword && (
                                                <span className='absolute inset-y-0 right-0 px-3 py-3'>
                                                    <DoneIcon fontSize='small' className='text-green-500' />
                                                </span>
                                            )}
                                            {isError.resetOfflineClientPassword && validationMessages.resetOfflineClientPassword && (
                                                <span className='absolute inset-y-0 right-0 px-3 py-3'>
                                                    <ErrorIcon fontSize='small' className='text-red-500' />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.change-only-if-offline-password-need-to-be-reset')}.</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.enable-licensing')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.enable-licensing')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.in-facialsense-enable-this-setting-to-active-standard-client')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.enableLicensing}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="enableLicensing"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.if-you-have-premium-client')}.</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.show-work-hour-in-clients')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.show-work-hours-in-clients')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.allow-displaying-work-hours-after-check-in-check-out-event')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.showWorkHoursInClients}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="showWorkHoursInClients"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.allow-displaying-work-hours-after-check-in-check-out-events')}.</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.allow-automatic-peripheral-enabling-of-new-clients')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('common.allow-automatic-peripheral-enabling-of-new-clients')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('common.if-this-setting-is-enabled-the-peripherals-in-the-resgisterd-devices')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.allowAutomaticPeripheralEnablingOfNewClient}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="allowAutomaticPeripheralEnablingOfNewClient"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.allow-enabling-all-peripherals-in-a-newly')}</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.offline-operations')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('tool-tip.offline-operation-mode')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('tool-tip.enable-to-record-attendance-in-facialsense')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.offlineOperations}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="offlineOperations"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.enable-offline-mode-in-client-apps')}</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.manual-capture-start')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.manualCaptureStart}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="manualCaptureStart"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.shows-manual-event-selection-on-client-application')}.</p>
                                    <p className='text-[#3B82F6] italic'>{t('common.always-enabled-for-facialsense')}</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.automatically-synchronize-offline-data')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('common.automatically-synchronize-offline-data')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('common.synchronize-the-offline-data-automatically-with-the-facialsense')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.automaticallySynchronizeOfflineData}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="automaticallySynchronizeOfflineData"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.automatically-synchronize-offline-data-with-server')}.
                                    </p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.hat-detection-feature')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.hatDetectionFeature}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="hatDetectionFeature"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>

                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.hide-user-list-on-client-apps')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.hideUserListOnClientApps}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="hideUserListOnClientApps"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>

                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.kiosk-mode-operation')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.kioskModeOperation}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="kioskModeOperation"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>

                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.cancel-event-button-on-client-event-feedback')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.cancelEventButtonOnClientEventFeedback}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="cancelEventButtonOnClientEventFeedback"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-7 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.whether-it-is-allowed-to-cancel-event-from-attendance')}.</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.multi-face-mode')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.multiFaceMode}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="multiFaceMode"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-7 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.enable-this-setting-to-detect-multiple')}.</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.use-in-built-biometric-verification-on-personal-clients')}
                                        </p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.useInBuiltBiometricVerificationOnPersonalClients}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="useInBuiltBiometricVerificationOnPersonalClients"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.self-enroll-templates')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={clientFormData.selfEnrollTemplates}
                                            onChange={(e) => handleSwitchChange(e, clientFormData, setClientFormData)}
                                            name="selfEnrollTemplates"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-7 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.self-enroll-first-template-from-client-device')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={1}>
                    <div className='biometric-tab flex flex-col gap-5'>
                        <div className='flex flex-col gap-5'>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 gap-y-3 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.face-liveness-mode-for-client-applications')}</p>
                                    </div>
                                    <div className='min-w-60'>
                                        <FormControl className='w-full'>
                                            <CustomSelect
                                                name="faceLivenessModeForClientApplications"
                                                id='faceLivenessModeForClientApplications'
                                                value={biometricFormData.faceLivenessModeForClientApplications}
                                                onChange={(e) => handleInputChange(e, biometricFormData, setBiometricFormData)}
                                                options={faceLivenessModeForClientApplicationsData}
                                                defaultValue="none"
                                                dataType="faceLivenessModeForClientApplications"
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end gap-3'>
                                    <ControlpanelSuccessButton>
                                        <SaveIcon />
                                    </ControlpanelSuccessButton>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1 pl-10'>
                                        <p>{t('common.passive-liveness-quality-threshold')}</p>
                                    </div>
                                    <div className='min-w-60'>
                                        <CustomInput
                                            labelEnable={false}
                                            inputType="number"
                                            name="passiveLivenessQualityThreshold"
                                            id="passiveLivenessQualityThreshold"
                                            value={biometricFormData.passiveLivenessQualityThreshold}
                                            handleInputChange={(e) => handleInputChange(e, biometricFormData, setBiometricFormData)}
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end gap-3'>
                                    <ControlpanelSuccessButton>
                                        <SaveIcon />
                                    </ControlpanelSuccessButton>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center '>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.liveness-threshold')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('common.face-liveness-threshold-for-client-application')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('common.the-value-which-control-the-requirement-for-client-side-face-liveness')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60'>
                                        <CustomInput
                                            labelEnable={false}
                                            inputType="number"
                                            name="livenessThreshold"
                                            id="livenessThreshold"
                                            value={biometricFormData.livenessThreshold}
                                            handleInputChange={(e) => handleInputChange(e, biometricFormData, setBiometricFormData)}
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>

                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 gap-y-3 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.face-quality')}</p>
                                    </div>
                                    <div className='min-w-60'>
                                        <CustomInput
                                            labelEnable={false}
                                            inputType="number"
                                            name="faceQuality"
                                            id="faceQuality"
                                            value={biometricFormData.faceQuality}
                                            handleInputChange={(e) => handleInputChange(e, biometricFormData, setBiometricFormData)}
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end gap-3'>
                                    <ControlpanelSuccessButton>
                                        <SaveIcon />
                                    </ControlpanelSuccessButton>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1 pl-10'>
                                        <p>{t('common.mask-detection')}</p>
                                    </div>
                                    <div className='min-w-32'>
                                        <FormControl className='w-full'>
                                            <CustomSelect
                                                name="maskDetection"
                                                id='maskDetection'
                                                value={biometricFormData.maskDetection}
                                                onChange={(e) => handleInputChange(e, biometricFormData, setBiometricFormData)}
                                                options={maskDetectionData}
                                                defaultValue="disable"
                                                dataType="maskDetection"
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.manual-face-selection')}</p>
                                        <DefaultTooltip
                                            readMore={true}
                                            title={t('common.manual-face-selection')}
                                            content={
                                                <div className='flex flex-col gap-3'>
                                                    <p>{t('common.this-allows-to-select-users-for-check-in-check-out-when-the-multile-face-capturing-mode')}.</p>
                                                </div>
                                            }>
                                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                                        </DefaultTooltip>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={biometricFormData.manualFaceSelection}
                                            onChange={(e) => handleSwitchChange(e, biometricFormData, setBiometricFormData)}
                                            name="manualFaceSelection"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>
                                <div className='col-span-12 pt-3'>
                                    <p className='text-[#3B82F6] italic'>{t('common.enable-or-disable-manual-face-selection')}</p>
                                </div>
                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.server-side-face-liveness-checking')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <IOSSwitch
                                            checked={biometricFormData.serverSideFaceLivenessChecking}
                                            onChange={(e) => handleSwitchChange(e, biometricFormData, setBiometricFormData)}
                                            name="serverSideFaceLivenessChecking"
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>

                            </div>
                            <PrimaryDivider />
                            <div className='grid grid-cols-12 items-center'>
                                <div className='col-span-9 flex justify-between items-center'>
                                    <div className='flex items-center gap-1'>
                                        <p>{t('common.server-side-face-liveness-confidence-threshold')}</p>
                                    </div>
                                    <div className='min-w-60 flex justify-end'>
                                        <CustomInput
                                            labelEnable={false}
                                            inputType="number"
                                            name="serverFaceLivenessConfidenceThreshold"
                                            id="serverFaceLivenessConfidenceThreshold"
                                            value={biometricFormData.serverFaceLivenessConfidenceThreshold}
                                            handleInputChange={(e) => handleInputChange(e, biometricFormData, setBiometricFormData)}
                                        />
                                    </div>
                                </div>
                                <div className='col-span-3 flex justify-end'>
                                    <ControlpanelSecondaryButton>
                                        <RefreshIcon />
                                    </ControlpanelSecondaryButton>
                                </div>

                            </div>
                        </div>
                    </div>
                </CustomTabPanel>
            </DialogContent>
            <DialogActions>
                <div className='flex gap-2 px-4 py-4'>
                    <ControlpanelSecondaryButton onClick={props.handleClose}>
                        <CloseIcon />
                        <p>{t('button.close')}</p>
                    </ControlpanelSecondaryButton>
                </div>
            </DialogActions>
        </CustomDialog>
    )
}

export default DeviceSpecificSettingsDialog